import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { GroupBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { GROUPS_SORT_BY_MAP } from "@components/groups/groups-constants";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import { getSortByFromGridSortModel } from "@components/table/groups/groups-table-utils";
import { useGroupsColumns } from "@components/table/groups/use-groups-table-columns";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { GroupsSortBy, GroupTypes } from "@custom-types/group-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { useAppDispatch } from "@store/store-helper";
import { setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect, useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** The groups that is shown in the table */
  groups: GroupTypes[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: GroupBulkButtonComponents;

  /** Groups sort criteria */
  sortBy: GroupsSortBy;

  /** Callback to update the sort criteria */
  setSortBy?: (sortBy: GroupsSortBy) => void;
}

/** Listing all the groups of a company */
export function GroupsTable({
  companyId,
  groups,
  isLoading,
  bulkActionButtons,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const { navigateToGroupDetails } = useAppNavigation();
  const dispatch = useAppDispatch();

  const handleColumnOrderChange = useHandleColumnOrderChange(TableType.groups);

  const columns = useGroupsColumns();

  /** Convert the sortBy prop to a GridSortModel for the table UI */
  const sortModel = useMemo(() => [GROUPS_SORT_BY_MAP[sortBy]], [sortBy]);

  // Callback fired when the sort option changes in list view
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      if (!setSortBy) {
        return;
      }

      setSortBy(newSortBy);
    },
    [setSortBy]
  );

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.groups));
  }, [dispatch]);

  return (
    <FaroTable
      entities={groups}
      uniqueIdKey={"id"}
      isLoading={isLoading}
      columns={columns}
      onRowClick={(group) =>
        navigateToGroupDetails({
          companyId,
          groupId: group.id,
        })
      }
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      tableType={TableType.groups}
      onSortModelChange={handleSortModelChange}
      sortTrackingSubject="Group"
      sortModel={sortModel}
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={GROUPS_TABLE_COLUMN_LABELS}
          disabledColumns={["name"]}
          tableType={TableType.groups}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
